import { useState, useCallback } from 'react';

interface UseDragAndDropParams {
  tempTickerList: string[];
  setTempTickerList: (list: string[]) => void;
  isEditingTickers: boolean;
}

interface UseDragAndDropReturn {
  draggedTicker: string | null;
  dragOverTicker: string | null;
  droppedTicker: string | null;
  handleDragStart: (e: React.DragEvent, ticker: string) => void;
  handleDragOver: (e: React.DragEvent, ticker: string) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent, targetTicker: string) => void;
  handleDragEnd: () => void;
}

type DropBehavior = 'swap' | 'insert';

/**
 * Hook for managing drag and drop handlers for List* pages
 * Supports two behaviors:
 * - 'swap': Swaps two items (used by ListAnalystViews)
 * - 'insert': Moves item to new position (used by others)
 */
export const useDragAndDrop = ({
  tempTickerList,
  setTempTickerList,
  isEditingTickers,
}: UseDragAndDropParams, dropBehavior: DropBehavior = 'insert'): UseDragAndDropReturn => {
  const [draggedTicker, setDraggedTicker] = useState<string | null>(null);
  const [dragOverTicker, setDragOverTicker] = useState<string | null>(null);
  const [droppedTicker, setDroppedTicker] = useState<string | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, ticker: string) => {
    // Some pages check isEditingTickers here, but we'll enforce it in handleDragOver/Drop instead
    // to maintain consistency with existing behavior
    setDraggedTicker(ticker);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', ticker);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, ticker: string) => {
    if (!isEditingTickers || !draggedTicker) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTicker(ticker);
  }, [isEditingTickers, draggedTicker]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!isEditingTickers) return;
    // Only clear if we're leaving the entire drag area
    // This handles the case where dragLeave fires when moving between child elements
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverTicker(null);
    }
  }, [isEditingTickers]);

  const handleDrop = useCallback((e: React.DragEvent, targetTicker: string) => {
    if (!isEditingTickers || !draggedTicker || draggedTicker === targetTicker) return;
    
    e.preventDefault();
    
    const newTickerList = [...tempTickerList];
    const draggedIndex = newTickerList.indexOf(draggedTicker);
    const targetIndex = newTickerList.indexOf(targetTicker);
    
    if (dropBehavior === 'swap') {
      // Swap positions (ListAnalystViews behavior)
      [newTickerList[draggedIndex], newTickerList[targetIndex]] = 
        [newTickerList[targetIndex], newTickerList[draggedIndex]];
      
      setTempTickerList(newTickerList);
      setDroppedTicker(targetTicker);
      
      // Clear drop animation after a short delay
      setTimeout(() => setDroppedTicker(null), 300);
    } else {
      // Insert behavior (default - used by ListTechnicals, ListDaily, ListFundamentals)
      newTickerList.splice(draggedIndex, 1);
      const insertIndex = draggedIndex < targetIndex ? targetIndex - 1 : targetIndex;
      newTickerList.splice(insertIndex, 0, draggedTicker);
      
      setTempTickerList(newTickerList);
      
      // Trigger drop-in animation
      setDroppedTicker(draggedTicker);
      setTimeout(() => {
        setDroppedTicker(null);
      }, 600); // Animation duration
    }
    
    setDraggedTicker(null);
    setDragOverTicker(null);
  }, [isEditingTickers, draggedTicker, tempTickerList, setTempTickerList, dropBehavior]);

  const handleDragEnd = useCallback(() => {
    setDraggedTicker(null);
    setDragOverTicker(null);
  }, []);

  return {
    draggedTicker,
    dragOverTicker,
    droppedTicker,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
  };
};

